import type { WizardAnswers } from "@/types";
import { asList, hasProduct } from "@/engine/answers";

export function showLicense(answers: WizardAnswers): boolean {
  return (
    hasProduct(answers, "rw") ||
    hasProduct(answers, "claroread") ||
    hasProduct(answers, "mindview")
  );
}

export function showRwVersion(answers: WizardAnswers): boolean {
  return hasProduct(answers, "rw");
}

export function showMvVersion(answers: WizardAnswers): boolean {
  return hasProduct(answers, "mindview");
}

export function showMvAssistPlatforms(answers: WizardAnswers): boolean {
  return hasProduct(answers, "mindview") && asList(answers.mv_version).includes("assist");
}

export function showTasksOs(answers: WizardAnswers): boolean {
  return hasProduct(answers, "tasks");
}

export function showTasksIntune(answers: WizardAnswers): boolean {
  return hasProduct(answers, "tasks") && asList(answers.tasks_os).includes("win");
}

export function showOffline(answers: WizardAnswers): boolean {
  if (answers.rw_license === "single" || !hasProduct(answers, "rw")) return false;
  const versions = asList(answers.rw_version);
  return versions.includes("win") || versions.includes("mac");
}

export function showNetworkRestricted(answers: WizardAnswers): boolean {
  if (answers.rw_license === "single") return false;
  const rwVersions = asList(answers.rw_version);
  const tasksOs = asList(answers.tasks_os);
  const rwNeedsNetwork =
    hasProduct(answers, "rw") &&
    (rwVersions.includes("chrome_edge") ||
      rwVersions.includes("ios") ||
      ((rwVersions.includes("win") || rwVersions.includes("mac")) &&
        answers.offline !== "yes"));
  const tasksNeedsNetwork = hasProduct(answers, "tasks") && tasksOs.length > 0;
  return rwNeedsNetwork || hasProduct(answers, "mindview") || tasksNeedsNetwork;
}

export function showUseOnWebpages(answers: WizardAnswers): boolean {
  if (answers.rw_license === "single" || !hasProduct(answers, "rw")) return false;
  return asList(answers.rw_version).includes("mac") && answers.offline !== "yes";
}

export function showLoginMethod(answers: WizardAnswers): boolean {
  if (answers.rw_license === "single") return false;
  const rwVersions = asList(answers.rw_version);
  const mvVersions = asList(answers.mv_version);
  const rwDesktop =
    hasProduct(answers, "rw") &&
    (rwVersions.includes("win") || rwVersions.includes("mac")) &&
    answers.offline !== "yes";
  const rwBrowser = hasProduct(answers, "rw") && rwVersions.includes("chrome_edge");
  const mvDesktop =
    hasProduct(answers, "mindview") &&
    (mvVersions.includes("win") || mvVersions.includes("mac"));
  return rwDesktop || rwBrowser || mvDesktop;
}

export function showMvRestrict(answers: WizardAnswers): boolean {
  const versions = asList(answers.mv_version);
  return (
    hasProduct(answers, "mindview") &&
    (versions.includes("win") || versions.includes("mac")) &&
    answers.login_method !== "auto"
  );
}

export function showTasksStartMenu(): boolean {
  return false;
}

export function showTasksBrowser(answers: WizardAnswers): boolean {
  if (!hasProduct(answers, "tasks")) return false;
  return asList(answers.tasks_os).includes("chromeos");
}

export const visibilityById: Record<string, (answers: WizardAnswers) => boolean> = {
  products: () => true,
  rw_license: showLicense,
  rw_version: showRwVersion,
  mv_version: showMvVersion,
  mv_assist_platforms: showMvAssistPlatforms,
  tasks_os: showTasksOs,
  tasks_intune: showTasksIntune,
  offline: showOffline,
  network_restricted: showNetworkRestricted,
  use_on_webpages: showUseOnWebpages,
  login_method: showLoginMethod,
  mv_win_restrict_online_services: showMvRestrict,
  tasks_start_menu: showTasksStartMenu,
  tasks_browser: showTasksBrowser,
};

export function isQuestionVisible(id: string, answers: WizardAnswers): boolean {
  const predicate = visibilityById[id];
  return predicate ? predicate(answers) : true;
}
